import { useState } from 'react';
import { useRecords, Drawer, Text, Area, Pick, Empty, Err, ago } from '../ui.js';
import {
  updateRecord, sendEmail, drainOutbox, ApiError, DOC_KINDS, RELATIONSHIPS,
} from '../api.js';
import type { Document, CompanyDetail } from '../api.js';
import { DomainCheck } from './DomainCheck.js';

// Panels shared by the account, deal, and contact records. Written once because
// "documents on this thing" is the same component three times over, and a fix to
// link handling should not need finding in three places.

// --- documents ---------------------------------------------------------------

export function DocumentsPanel({ companyId, dealId, contactId }: {
  companyId?: string; dealId?: string; contactId?: string;
}) {
  const scope = dealId ? { deal_id: dealId }
    : contactId ? { contact_id: contactId }
    : { company_id: companyId };
  const docs = useRecords<Document & { id: string }>('documents', scope);
  const [adding, setAdding] = useState(false);

  return (
    <>
      {docs.rows?.length === 0 && (
        <Empty
          title="No documents"
          hint="Link the proposal, the deck, the call transcript — anything you would go hunting for later."
          action={<button className="primary" onClick={() => setAdding(true)}>Add a document</button>}
        />
      )}

      {(docs.rows?.length ?? 0) > 0 && (
        <>
          {docs.rows?.map((d) => (
            <div className="row" key={d.id}>
              <span className="rname">
                {d.url
                  ? <a href={d.url} target="_blank" rel="noreferrer">{d.name}</a>
                  : d.name}
              </span>
              <span className="flag flag-mute">{d.kind.replace('_', ' ')}</span>
              <span className="meta">{ago(d.created_at)}</span>
              <span className="flags">
                <button className="mini" onClick={() => void docs.remove(d.id)}>Remove</button>
              </span>
            </div>
          ))}
          <div className="actions">
            <button onClick={() => setAdding(true)}>Add a document</button>
          </div>
        </>
      )}

      {adding && (
        <AddDocument
          scope={{ company_id: companyId ?? null, deal_id: dealId ?? null,
                   contact_id: contactId ?? null }}
          onClose={() => setAdding(false)}
          onSave={(d) => docs.create(d)}
        />
      )}
    </>
  );
}

function AddDocument({ scope, onClose, onSave }: {
  scope: Record<string, string | null>;
  onClose: () => void; onSave: (d: Record<string, unknown>) => Promise<unknown>;
}) {
  const [f, setF] = useState({ name: '', url: '', kind: 'proposal', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Drawer
      title="Add a document"
      subtitle="A link today — file upload needs a storage bucket"
      onClose={onClose}
      footer={
        <button className="primary" disabled={saving || !f.name.trim()} onClick={async () => {
          setSaving(true); setError(null);
          try { await onSave({ ...scope, ...f, storage: 'link' }); onClose(); }
          catch (e) { setError(e instanceof ApiError ? e.message : String(e)); setSaving(false); }
        }}>{saving ? 'Saving…' : 'Add document'}</button>
      }
    >
      <Err>{error}</Err>
      <Text label="Name" value={f.name} autoFocus placeholder="Payra — demand build proposal v2"
        onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} />
      <Text label="Link" value={f.url} placeholder="https://… (OneDrive, Docs, anywhere)"
        onChange={(e) => setF((p) => ({ ...p, url: e.target.value }))} />
      <Pick label="Kind" value={f.kind} allowEmpty={false}
        options={DOC_KINDS.map((k) => ({ value: k, label: k.replace('_', ' ') }))}
        onChange={(v) => setF((p) => ({ ...p, kind: v }))} />
      <Area label="Why it matters" rows={3} value={f.notes}
        onChange={(v) => setF((p) => ({ ...p, notes: v }))} />
    </Drawer>
  );
}

// --- email log ------------------------------------------------------------------

/**
 * Every message on this account or contact, newest first.
 *
 * Scoped by whichever id is passed, so the account sees the whole committee's
 * mail and a contact sees only theirs. Both were missing: mail existed in the
 * database with nowhere to read it, which is the same as not having it.
 */
export function EmailLogPanel({ companyId, contactId, onCompose, onEdit }: {
  companyId?: string; contactId?: string;
  onCompose?: () => void;
  onEdit?: (m: EmailRow) => void;
}) {
  const scope = contactId ? { contact_id: contactId } : { company_id: companyId };
  const emails = useRecords<EmailRow>('emails', scope);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows = emails.rows ?? [];
  const queued = rows.filter((m) => m.status === 'scheduled');

  return (
    <>
      <Err>{error}</Err>

      {queued.length > 0 && (
        <div className="seqbar armed">
          <div className="seqbar-text">
            <strong>{queued.length} queued</strong>
            <span>Written and waiting. Nothing leaves until you send it.</span>
          </div>
          <div className="seqbar-actions">
            <button className="primary" disabled={busy === 'all'} onClick={async () => {
              setBusy('all'); setError(null);
              try {
                // Drains the whole outbox, not just this account — the daily cap
                // is global and pretending otherwise would overshoot it.
                const r = await drainOutbox();
                if (r.failed) setError(`${r.failed} failed to send.`);
                void emails.reload();
              } catch (e) {
                setError(e instanceof ApiError ? e.message : String(e));
              } finally { setBusy(null); }
            }}>{busy === 'all' ? 'Sending…' : `Send ${queued.length}`}</button>
          </div>
        </div>
      )}

      {rows.length === 0 && (
        <Empty
          title="No email yet"
          hint="Everything sent, queued, or drafted for this record shows here."
          action={onCompose && <button className="primary" onClick={onCompose}>Write one</button>}
        />
      )}

      {rows.map((m) => (
        <div className="event" key={m.id}>
          <div className="row">
            <span className="rname">{m.subject ?? '(no subject)'}</span>
            <span className={`flag ${
              m.status === 'sent' ? 'flag-ok'
              : m.status === 'failed' || m.status === 'bounced' ? 'flag-block'
              : m.status === 'draft' ? 'flag-mute' : 'flag-warn'}`}>{m.status}</span>
            {m.direction === 'inbound' && <span className="flag flag-ok">reply</span>}
            {m.opened_at && <span className="flag flag-ok">opened</span>}
            {m.click_count > 0 && <span className="flag flag-ok">{m.click_count} click</span>}
            <span className="meta">{m.to_name ?? m.to_email ?? ''}</span>
            <span className="meta">{ago(m.sent_at ?? m.created_at)}</span>
            <span className="flags">
              {(m.status === 'draft' || m.status === 'scheduled') && onEdit && (
                <button className="mini" onClick={() => onEdit(m)}>Edit</button>
              )}
              {m.status === 'scheduled' && (
                <button className="mini" disabled={busy === m.id} onClick={async () => {
                  setBusy(m.id); setError(null);
                  try {
                    const r = await sendEmail(m.id);
                    if (r.failed) setError(r.results?.[0]?.error ?? 'Send failed.');
                    void emails.reload();
                  } catch (e) {
                    setError(e instanceof ApiError ? e.message : String(e));
                  } finally { setBusy(null); }
                }}>{busy === m.id ? 'Sending…' : 'Send'}</button>
              )}
            </span>
          </div>
          {m.error && <p className="meta" style={{ color: 'var(--block)' }}>{m.error}</p>}
          {m.body && <pre className="emailbody">{m.body}</pre>}
        </div>
      ))}

      {rows.length > 0 && onCompose && (
        <div className="actions"><button onClick={onCompose}>Write another</button></div>
      )}
    </>
  );
}

interface EmailRow {
  id: string; subject: string | null; body: string | null; status: string;
  direction: string; to_email: string | null; to_name: string | null;
  sent_at: string | null; created_at: string; opened_at: string | null;
  click_count: number; error: string | null; body_html?: string | null;
}

// --- notes ------------------------------------------------------------------------

/**
 * Notes.
 *
 * Stored as activities with kind 'note' rather than a separate table, so a note
 * appears in the timeline alongside the call it followed. A parallel notes table
 * would mean the timeline was missing half the story and nobody would trust it.
 */
export function NotesPanel({ companyId, contactId }: {
  companyId: string; contactId?: string;
}) {
  const notes = useRecords<{
    id: string; summary: string; detail: string | null; occurred_at: string;
  }>('activities', contactId ? { contact_id: contactId, kind: 'note' } : { company_id: companyId, kind: 'note' });
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    const t = text.trim();
    if (!t) return;
    setSaving(true); setError(null);
    try {
      // First line is the summary so the timeline row reads well; the rest is
      // detail. Saves making the operator fill in two fields to write one thought.
      const [first, ...rest] = t.split('\n');
      await notes.create({
        company_id: companyId, contact_id: contactId ?? null,
        kind: 'note', category: 'sales',
        summary: first.slice(0, 200), detail: rest.join('\n') || null,
        occurred_at: new Date().toISOString(),
      });
      setText('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setSaving(false); }
  }

  return (
    <>
      <Err>{error}</Err>
      <Area label="Add a note" rows={4} value={text} onChange={setText}
        placeholder="What you learned, what they said, what you promised." />
      <div className="actions">
        <button className="primary" disabled={saving || !text.trim()} onClick={() => void add()}>
          {saving ? 'Saving…' : 'Save note'}
        </button>
        <span className="cost">Also appears in the timeline</span>
      </div>

      {notes.rows?.length === 0 && <p className="meta">No notes yet.</p>}
      {notes.rows?.map((n) => (
        <div className="event" key={n.id}>
          <div className="row">
            <span className="rname">{n.summary}</span>
            <span className="meta">{ago(n.occurred_at)}</span>
            <span className="flags">
              <button className="mini" onClick={() => void notes.remove(n.id)}>Delete</button>
            </span>
          </div>
          {n.detail && <p className="meta" style={{ whiteSpace: 'pre-wrap' }}>{n.detail}</p>}
        </div>
      ))}
    </>
  );
}

// --- sending identity ----------------------------------------------------------

/**
 * Who outreach goes out as.
 *
 * Two rules are worth stating in the UI because getting them wrong is silent.
 * Cold outreach must not share a domain with transactional alerts — burning
 * notify.hamermarketing.com takes the daily reports down with it. And an
 * unverified domain does not bounce; it lands in spam and you find out a
 * quarter later, which is why the send engine refuses on anything but
 * 'verified'.
 */
export function SendingPanel() {
  const ids = useRecords<{
    id: string; domain: string; from_name: string | null; from_email: string;
    reply_to: string | null; status: string; daily_send_cap: number; is_default: boolean;
    postal_address: string | null;
  }>('sending_identities');
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({
    from_name: '', from_email: '', reply_to: '', daily_send_cap: '15',
    postal_address: '',
  });
  const [error, setError] = useState<string | null>(null);

  const domainOf = (email: string) => email.split('@')[1]?.trim().toLowerCase() ?? '';
  const warnsNotify = domainOf(f.from_email).includes('notify.');

  return (
    <section className="panel">
      <h4>Sending identity</h4>

      {/* Ask Resend rather than trusting the checkbox — see DomainCheck.tsx. */}
      <DomainCheck />

      {ids.rows?.length === 0 && !adding && (
        <Empty
          title="No sending identity"
          hint="Nothing can send until outreach has a From address. It must not be the notify subdomain — that one carries your alerts."
          action={<button className="primary" onClick={() => setAdding(true)}>Add one</button>}
        />
      )}

      {ids.rows?.map((i) => (
        <div className="row" key={i.id}>
          <span className="rname">{i.from_name ? `${i.from_name} <${i.from_email}>` : i.from_email}</span>
          <span className="meta">{i.daily_send_cap}/day cold</span>
          <span className={`flag ${i.status === 'verified' ? 'flag-ok' : 'flag-warn'}`}>
            {i.status}
          </span>
          {/* THE ADDRESS THAT GOES UNDER THIS SENDER'S COLD MAIL, and it is
              per-sender because the mail is. Shown on the row rather than
              behind an edit button: a missing one stops this identity sending
              cold, and a requirement you have to click to discover is one you
              discover at the moment of refusal. */}
          <span className={`flag ${String(i.postal_address ?? '').trim() ? 'flag-ok' : 'flag-warn'}`}>
            {String(i.postal_address ?? '').trim() ? 'address set' : 'no postal address'}
          </span>
          <span className="flags">
            {i.status !== 'verified' && (
              <button className="mini"
                title="Only after the domain is verified in Resend. Sending refuses until then."
                onClick={() => void ids.update(i.id, { status: 'verified' })}>
                Mark verified
              </button>
            )}
            <button className="mini" onClick={() => void ids.remove(i.id)}>Remove</button>
          </span>
          <label className="field">
            <span className="field-label">Postal address on this sender's cold mail</span>
            <input
              type="text"
              defaultValue={String(i.postal_address ?? '')}
              placeholder="Company, street, city, state, postcode"
              onBlur={(e) => {
                if (e.target.value.trim() !== String(i.postal_address ?? '').trim()) {
                  void ids.update(i.id, { postal_address: e.target.value.trim() || null });
                }
              }}
            />
          </label>
        </div>
      ))}

      {(ids.rows?.length ?? 0) > 0 && !adding && (
        <div className="actions"><button onClick={() => setAdding(true)}>Add another</button></div>
      )}

      {adding && (
        <>
          <Err>{error}</Err>
          {warnsNotify && (
            <p className="warnbox">
              That is the alerts subdomain. Cold outreach sent from it puts your daily
              reports and MCP health mail at risk. Use the root domain instead.
            </p>
          )}
          <div className="pair">
            <Text label="From name" value={f.from_name} placeholder="Your name"
              onChange={(e) => setF((p) => ({ ...p, from_name: e.target.value }))} />
            <Text label="From address" value={f.from_email} placeholder="you@yourcompany.com"
              onChange={(e) => setF((p) => ({ ...p, from_email: e.target.value }))} />
          </div>
          <div className="pair">
            <Text label="Reply-to" value={f.reply_to} hint="Blank uses the From address."
              onChange={(e) => setF((p) => ({ ...p, reply_to: e.target.value }))} />
            <Text label="Daily cold cap" type="number" value={f.daily_send_cap}
              hint="The playbook default is 15, the ceiling is 50. One-to-one mail is capped separately."
              onChange={(e) => setF((p) => ({ ...p, daily_send_cap: e.target.value }))} />
          </div>
          <Text label="Postal address" value={f.postal_address}
            placeholder="Company, street, city, state, postcode"
            hint="Printed at the bottom of every cold message from this sender, beside the unsubscribe link. Required by law; a PO box or registered agent is fine."
            onChange={(e) => setF((p) => ({ ...p, postal_address: e.target.value }))} />
          <div className="actions">
            <button className="primary" disabled={!f.from_email.includes('@')} onClick={async () => {
              setError(null);
              try {
                await ids.create({
                  domain: domainOf(f.from_email),
                  from_name: f.from_name, from_email: f.from_email,
                  reply_to: f.reply_to || null,
                  postal_address: f.postal_address.trim() || null,
                  daily_send_cap: Number(f.daily_send_cap) || 15,
                  is_default: (ids.rows?.length ?? 0) === 0,
                  status: 'unverified',
                });
                setAdding(false);
              } catch (e) {
                setError(e instanceof ApiError ? e.message : String(e));
              }
            }}>Save identity</button>
            <button onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </>
      )}
    </section>
  );
}

// --- company edit -------------------------------------------------------------

const STAGE_OPTS = [
  'identified', 'qualified', 'warming', 'engaged', 'meeting_set',
  'opportunity', 'won', 'lost', 'nurture', 'watch',
].map((v) => ({ value: v, label: v.replace('_', ' ') }));

/** Read an optional column off a record whose type predates it. */
function str(o: unknown, k: string): string {
  const v = (o as Record<string, unknown>)[k];
  return v == null ? '' : String(v);
}

/** "12M" / "12,000,000" / "$12m" -> 12000000. Empty -> null, never 0. */
function parseMoney(raw: string): number | null {
  const t = raw.trim().replace(/[$,\s]/g, '');
  if (!t) return null;
  const m = /^(\d+(?:\.\d+)?)([kmb])?$/i.exec(t);
  if (!m) return null;
  const mult = { k: 1e3, m: 1e6, b: 1e9 }[(m[2] ?? '').toLowerCase()] ?? 1;
  return Math.round(Number(m[1]) * mult);
}

export function EditCompany({ c, onClose, onSaved }: {
  c: CompanyDetail; onClose: () => void; onSaved: () => void;
}) {
  const [f, setF] = useState({
    name: c.name, url: c.url ?? '', sub_category: c.sub_category ?? '',
    hq: c.hq ?? '', employees: c.employees ? String(c.employees) : '',
    stage: c.stage, priority: c.priority ?? '',
    industry: str(c, 'industry'), city: str(c, 'city'), state: str(c, 'state'),
    country: str(c, 'country'), phone: str(c, 'phone'),
    linkedin_url: str(c, 'linkedin_url'), revenue: str(c, 'revenue'),
    relationship: (c as unknown as { relationship?: string }).relationship ?? 'cold',
    relationship_note: (c as unknown as { relationship_note?: string }).relationship_note ?? '',
    gap_statement: c.gap_statement ?? '', wedge: c.wedge ?? '',
    next_action: c.next_action ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));

  return (
    <Drawer title={`Edit ${c.name}`} onClose={onClose} footer={
      <button className="primary" disabled={saving || !f.name.trim()} onClick={async () => {
        setSaving(true); setError(null);
        try {
          await updateRecord('companies', c.id, {
            ...f,
            employees: f.employees ? Number(f.employees) : null,
            // Typed as "12", "12M" or "12,000,000" — all three mean the same
            // thing to a person and only one of them is a number.
            revenue: parseMoney(f.revenue),
            // Keep the bare domain in step with the URL — every provider lookup
            // keys on it, and a stale one is worse than a missing one.
            domain: f.url.trim()
              ? f.url.trim().toLowerCase()
                  .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '')
              : null,
          });
          onSaved(); onClose();
        } catch (e) {
          setError(e instanceof ApiError ? e.message : String(e)); setSaving(false);
        }
      }}>{saving ? 'Saving…' : 'Save'}</button>
    }>
      <Err>{error}</Err>
      <Text label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} />
      <Text label="Website" value={f.url} placeholder="https://www.payra.com"
        hint="Enrichment and contact search are keyed on this."
        onChange={(e) => set('url', e.target.value)} />
      <div className="pair">
        <Text label="Industry" value={f.industry}
          hint="Scored against your ICP."
          onChange={(e) => set('industry', e.target.value)} />
        <Text label="Category" value={f.sub_category}
          onChange={(e) => set('sub_category', e.target.value)} />
      </div>
      <div className="pair">
        <Text label="Employees" type="number" value={f.employees}
          onChange={(e) => set('employees', e.target.value)} />
        <Text label="Revenue" value={f.revenue} placeholder="12M"
          hint="A number. 12M, 12000000 — either."
          onChange={(e) => set('revenue', e.target.value)} />
      </div>
      {/* Structured, because the ICP filters on city/state/country and a
          single "HQ" line cannot be compared to a list. `hq` stays for the
          hand-written detail a form field would flatten. */}
      <div className="pair">
        <Text label="City" value={f.city} onChange={(e) => set('city', e.target.value)} />
        <Text label="State / region" value={f.state}
          onChange={(e) => set('state', e.target.value)} />
      </div>
      <div className="pair">
        <Text label="Country" value={f.country}
          onChange={(e) => set('country', e.target.value)} />
        <Text label="Phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
      </div>
      <Text label="LinkedIn" value={f.linkedin_url}
        placeholder="linkedin.com/company/…"
        onChange={(e) => set('linkedin_url', e.target.value)} />
      <Text label="HQ note" value={f.hq}
        hint="Free text. Kept as-is — the structured fields above are what gets matched."
        onChange={(e) => set('hq', e.target.value)} />
      <div className="pair">
        <Pick label="Stage" value={f.stage} allowEmpty={false} options={STAGE_OPTS}
          onChange={(v) => set('stage', v)} />
        <Pick label="Priority" value={f.priority}
          options={['P1', 'P2', 'P3'].map((v) => ({ value: v, label: v }))}
          onChange={(v) => set('priority', v)} />
      </div>
      <Pick label="Relationship" value={f.relationship} allowEmpty={false}
        hint="How warm the room is. Survives a deal being lost — stage does not."
        options={RELATIONSHIPS.map((r) => ({ value: r, label: r.replace('_', ' ') }))}
        onChange={(v) => set('relationship', v)} />
      <Area label="Relationship note" rows={2} value={f.relationship_note}
        onChange={(v) => set('relationship_note', v)} />
      <Area label="Gap statement" rows={3} value={f.gap_statement}
        onChange={(v) => set('gap_statement', v)} />
      <Area label="Wedge" rows={2} value={f.wedge} onChange={(v) => set('wedge', v)} />
      <Text label="Next action" value={f.next_action}
        onChange={(e) => set('next_action', e.target.value)} />
    </Drawer>
  );
}
